import { GripVertical, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface Hint {
  id?: string
  content: string
  order: number
}

interface HintsSectionProps {
  hints: Hint[]
  onUpdate: (hints: Hint[]) => void
}

export function HintsSection({ hints, onUpdate }: HintsSectionProps) {
  const handleAddHint = () => {
    const newHint: Hint = {
      content: '',
      order: hints.length,
    }
    onUpdate([...hints, newHint])
  }

  const handleUpdateHint = (index: number, content: string) => {
    const updated = [...hints]
    updated[index] = { ...updated[index], content }
    onUpdate(updated)
  }

  const handleRemoveHint = (index: number) => {
    const updated = hints.filter((_, i) => i !== index)
    // Re-order remaining hints
    onUpdate(updated.map((h, i) => ({ ...h, order: i })))
  }

  const handleMoveHint = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === hints.length - 1)
    ) {
      return
    }

    const updated = [...hints]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]]
    onUpdate(updated.map((h, i) => ({ ...h, order: i })))
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button type="button" onClick={handleAddHint} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Hint
        </Button>
      </div>

      {hints.length === 0 ? (
        <div className="border-border bg-muted/50 flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
          <p className="text-muted-foreground mb-4">No hints added yet.</p>
          <Button type="button" onClick={handleAddHint} variant="outline">
            <Plus className="mr-2 h-4 w-4" />
            Add First Hint
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {hints.map((hint, index) => (
            <div
              key={hint.id || `hint-${index}`}
              className="border-border bg-muted/30 rounded-lg border p-3"
            >
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GripVertical className="text-muted-foreground h-4 w-4" />
                  <span className="text-sm font-medium">Hint {index + 1}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMoveHint(index, 'up')}
                    disabled={index === 0}
                    className="h-7 w-7 p-0"
                  >
                    ↑
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMoveHint(index, 'down')}
                    disabled={index === hints.length - 1}
                    className="h-7 w-7 p-0"
                  >
                    ↓
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveHint(index)}
                    className="text-destructive hover:bg-destructive/10 h-7 w-7 p-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <textarea
                value={hint.content}
                onChange={(e) => handleUpdateHint(index, e.target.value)}
                placeholder="Think about what data structure would help you quickly look up values..."
                className="border-input bg-background min-h-[80px] w-full resize-y rounded-md border px-3 py-2 text-sm"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
